import React from "react";
import { Column } from "primereact/column";
import { T } from "services/lang/LanguageService";
import { DataTable } from "primereact/datatable";
import { formatDisplayDatetime } from "utils/time-utils";
import { Button } from "primereact/button";
import { exportToCSV, generateTableData, WeatherTableProps } from "./WeatherMonitoringUtils";
import { WeatherMonitoringTableColumnRowData } from "../charts/interfaces";
import { DATE_FORMATTER_HOURS_MINUTES } from "utils/constants";
import { Timezones, useTimezone } from "hooks/useTimezone";

const tableColumns = [
  {
    field: "time",
    header: T("word.time"),
    body: (rowData: WeatherMonitoringTableColumnRowData, tz: Timezones) =>
      formatDisplayDatetime(rowData.time, tz.display, DATE_FORMATTER_HOURS_MINUTES),
    width: "5rem",
  },
  {
    field: "temperature",
    header: T("word.temperature"),
    width: "8rem",
    body: (rowData: WeatherMonitoringTableColumnRowData) => rowData.temperature,
  },
  {
    field: "windSpeed",
    header: T("word.wind-speed"),
    width: "8rem",
    body: (rowData: WeatherMonitoringTableColumnRowData) => rowData.windSpeed,
  },
  {
    field: "windDirection",
    header: T("word.wind-direction"),
    width: "8rem",
    body: (rowData: WeatherMonitoringTableColumnRowData) => (
      <div className="flex flex-row justify-content-between">
        <div>{rowData.windDirection}</div>
        <Button
          icon="pi pi-arrow-up"
          disabled
          className="p-button-text p-button-secondary p-0"
          style={{ transform: `rotate(${rowData.windDirection}deg)` }}
        />
      </div>
    ),
  },
  {
    field: "gusture",
    header: T("word.gusture"),
    width: "8rem",
    body: (rowData: WeatherMonitoringTableColumnRowData) => rowData.gusture,
  },
  {
    field: "precipitation",
    header: T("word.precipitation"),
    width: "8rem",
    body: (rowData: WeatherMonitoringTableColumnRowData) => rowData.precipitation,
  },
  {
    field: "humidity",
    header: T("word.humidity"),
    width: "8rem",
    body: (rowData: WeatherMonitoringTableColumnRowData) => rowData.humidity,
  },
  {
    field: "feelsLikeTemperature",
    header: T("word.feels-like"),
    width: "8rem",
    body: (rowData: WeatherMonitoringTableColumnRowData) => rowData.feelsLikeTemperature,
  },
];

const WeatherMonitoringTable = ({ datasetsData, props, dayOffset, endHour, tabHeader }: WeatherTableProps) => {
  const tz = useTimezone();

  const tableData = generateTableData(datasetsData, props, dayOffset, endHour);

  const handleExportCSV = () => {
    exportToCSV(tableData, `weather_data_${tabHeader}`, tz.company);
  };

  return (
    <div className="overflow-x-auto">
      <Button
        type="button"
        icon="pi pi-file"
        onClick={handleExportCSV}
        className="component-editor-button icon-blue"
        label="CSV"
      />
      <DataTable value={tableData} className="ml-5 w-max" showGridlines size="small" stripedRows>
        {tableColumns.map(col => (
          <Column
            key={col.field}
            field={col.field}
            header={col.header}
            body={d => col.body(d, tz)}
            style={{ width: col.width }}
          />
        ))}
      </DataTable>
    </div>
  );
};

export default WeatherMonitoringTable;
